import { toGraphQLBaseUnit } from "../../../Utils/toGraphQLBaseUnit";
import { SellingMode } from "../enums/sellingMode";
import { Unit } from "../enums/unit";
import { Product, ProductItem } from "../types/products";

const SELLING_MODE_LABEL_FALLBACKS: Record<SellingMode, string> = {
  [SellingMode.SINGLE_ITEM]: "Single Item",
  [SellingMode.UNIT_BASED]: "Unit Based (Piece)",
  [SellingMode.MEASURED]: "Measured"
};

/**
 * Formats SellingMode enum for display.
 * Pass `t` from `useKivunovaTranslation()` for translated labels in UI lists.
 */
export const formatSellingMode = (
  mode: SellingMode,
  t?: (key: string, fallback: string) => string
): string => {
  const fallback = SELLING_MODE_LABEL_FALLBACKS[mode] ?? String(mode);
  return t ? t(`selling-mode-label-${mode}`, fallback) : fallback;
};

/**
 * Formats Unit enum for display (aligned with `UNIT_DISPLAY_LABELS` in modals).
 */
export const formatUnit = (unit: Unit): string => {
  const unitMap: Record<Unit, string> = {
    [Unit.UNIT]: "Unit",
    [Unit.G]: "Gram (g)",
    [Unit.KG]: "Kilogram (kg)",
    [Unit.ML]: "Milliliter (ml)",
    [Unit.L]: "Liter (L)",
    [Unit.CM]: "Centimeter (cm)",
    [Unit.M]: "Meter (m)",
    [Unit.MM]: "Millimeter (mm)",
    [Unit.SQCM]: "Square Centimeter (cm²)",
    [Unit.OZ]: "Ounce (oz)",
    [Unit.LB]: "Pound (lb)",
    [Unit.BOTTLE]: "Bottle"
  };
  return unitMap[unit] || unit;
};

/**
 * Formats API/base-unit strings for product detail (handles legacy `LITER` / `METER`).
 */
export const formatBaseUnitForDisplay = (raw?: string | null): string => {
  if (raw == null || String(raw).trim() === "") return "-";
  const normalized = toGraphQLBaseUnit(raw) ?? String(raw).trim().toUpperCase();
  const asUnit = normalized as Unit;
  if (Object.values(Unit).includes(asUnit)) {
    return formatUnit(asUnit);
  }
  return raw;
};

/**
 * Formats date string for display
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

/**
 * Formats price/currency for display
 */
export const formatPrice = (price?: number): string => {
  if (price === undefined || price === null) return "-";
  return `$${price.toFixed(2)}`;
};

type TranslateFn = (key: string, fallback: string) => string;

/** English fallbacks for `product-item-status-*` translation keys. */
export const PRODUCT_ITEM_STATUS_LABEL_FALLBACKS: Record<string, string> = {
  CREATED: "Created",
  IN_STOCK: "In stock",
  RESERVED: "Reserved",
  SOLD: "Sold",
  DAMAGED: "Damaged",
  EXPIRED: "Expired",
  LOST: "Lost",
  CONSUMED: "Consumed"
};

/**
 * Formats ProductItemStatus for display. Pass `t` from `useKivunovaTranslation()` to localize.
 */
export const formatProductItemStatus = (
  status: string,
  t?: TranslateFn
): string => {
  const raw = (status ?? "").trim();
  if (!raw) return "-";
  const enumKey = raw.toUpperCase().replace(/\s+/g, "_");
  const fallback =
    PRODUCT_ITEM_STATUS_LABEL_FALLBACKS[enumKey] ??
    raw
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  const key = `product-item-status-${enumKey}`;
  return t ? t(key, fallback) : fallback;
};

/** i18n keys for product item detail labels — use with `t(key, PRODUCT_ITEM_DETAIL_FIELD_LABEL_FALLBACKS[key])`. */
export const PRODUCT_ITEM_DETAIL_FIELD_LABEL_FALLBACKS: Record<string, string> =
  {
    "product-item-detail-field-item-id": "Item ID",
    "product-item-detail-field-product-name": "Product Name",
    "product-item-detail-field-status": "Status",
    "product-item-detail-field-total-quantity": "Total Quantity",
    "product-item-detail-field-remaining-quantity": "Remaining Quantity",
    "product-item-detail-field-purchase-price": "Purchase Price",
    "product-item-detail-field-sales-price": "Sales Price",
    "product-item-detail-field-lot-number": "Lot Number",
    "product-item-detail-field-weight": "Weight",
    "product-item-detail-field-height": "Height",
    "product-item-detail-field-width": "Width",
    "product-item-detail-field-color": "Color",
    "product-item-detail-field-thickness": "Thickness",
    "product-item-detail-field-pack-size": "Pack Size",
    "product-item-detail-field-expiration-date": "Expiration Date",
    "product-item-detail-field-production-date": "Production Date",
    "product-item-detail-field-created-at": "Created At",
    "product-item-detail-field-updated-at": "Updated At",
    "product-item-detail-field-last-updated-by": "Last Updated By",
    "product-item-detail-field-store-id": "Store ID",
    "product-item-detail-field-company-id": "Company ID"
  };

/** i18n keys for product detail labels — use with `t(key, PRODUCT_DETAIL_FIELD_LABEL_FALLBACKS[key])`. */
export const PRODUCT_DETAIL_FIELD_LABEL_FALLBACKS: Record<string, string> = {
  "product-detail-field-product-id": "Product ID",
  "product-detail-field-name": "Name",
  "product-detail-field-description": "Description",
  "product-detail-field-category-id": "Category ID",
  "product-detail-field-category-path": "Category Path",
  "product-detail-field-brand-name": "Brand Name",
  "product-detail-field-supplier-id": "Supplier ID",
  "product-detail-field-selling-mode": "Selling Mode",
  "product-detail-field-base-unit": "Base Unit",
  "product-detail-field-partial-selling-allowed": "Partial Selling Allowed",
  "product-detail-field-status": "Status",
  "product-detail-field-image-url": "Image URL",
  "product-detail-field-company-id": "Company ID",
  "product-detail-field-store-id": "Store ID",
  "product-detail-field-created-by": "Created By",
  "product-detail-field-updated-by": "Updated By",
  "product-detail-field-created-at": "Created At",
  "product-detail-field-updated-at": "Updated At"
};

/**
 * Label keys whose values are raw user IDs.
 * The consuming component should render these through `<UserName userId={value} />`
 * instead of displaying the raw string.
 *
 * @example
 * fields.map(([key, value]) => (
 *   <td key={key}>
 *     {isUserIdField(key) ? <UserName userId={String(value ?? "")} /> : (value ?? "-")}
 *   </td>
 * ))
 */
export const USER_ID_FIELD_KEYS = new Set<string>([
  // Product detail
  "product-detail-field-created-by",
  "product-detail-field-updated-by",
  // Product item detail
  "product-item-detail-field-last-updated-by"
]);

/**
 * Returns true when the field's value is a raw user ID that should be
 * resolved to a human-readable name via `<UserName />`.
 */
export const isUserIdField = (key: string): boolean =>
  USER_ID_FIELD_KEYS.has(key);

/**
 * Gets the product detail fields array for display.
 * Each row is `[labelKey, value]` where `labelKey` maps to {@link PRODUCT_DETAIL_FIELD_LABEL_FALLBACKS}.
 * Rows whose key satisfies {@link isUserIdField} contain a raw user ID — render via `<UserName />`.
 */
export const getProductDetailFields = (
  product: Product
): [string, string | undefined | number | boolean][] => {
  return [
    ["product-detail-field-product-id", product.id],
    ["product-detail-field-name", product.name],
    ["product-detail-field-description", product.description],
    ["product-detail-field-category-id", product.category],
    ["product-detail-field-category-path", product.categoryPath],
    ["product-detail-field-brand-name", product.brandName],
    ["product-detail-field-supplier-id", product.supplierId],
    [
      "product-detail-field-selling-mode",
      formatSellingMode(product.sellingMode)
    ],
    [
      "product-detail-field-base-unit",
      formatBaseUnitForDisplay(product.baseUnit)
    ],
    [
      "product-detail-field-partial-selling-allowed",
      product.isPartialSellingAllowed ? "Yes" : "No"
    ],
    ["product-detail-field-status", product.status],
    ["product-detail-field-image-url", product.imageUrl || "-"],
    ["product-detail-field-company-id", product.companyId],
    ["product-detail-field-store-id", product.storeId ?? "-"],
    ["product-detail-field-created-by", product.createdBy],
    ["product-detail-field-updated-by", product.updatedBy],
    ["product-detail-field-created-at", formatDate(product.createdAt)],
    ["product-detail-field-updated-at", formatDate(product.updatedAt)]
  ];
};

/**
 * Gets the product item detail fields array for display.
 * Each row is `[labelKey, value]` where `labelKey` maps to {@link PRODUCT_ITEM_DETAIL_FIELD_LABEL_FALLBACKS}.
 * Rows whose key satisfies {@link isUserIdField} contain a raw user ID — render via `<UserName />`.
 */
export const getProductItemDetailFields = (
  productItem: ProductItem,
  product?: Product,
  t?: TranslateFn
): [string, string | undefined | number][] => {
  return [
    ["product-item-detail-field-item-id", productItem?.id],
    ["product-item-detail-field-product-name", product?.name],
    [
      "product-item-detail-field-status",
      formatProductItemStatus(productItem.state, t)
    ],
    ["product-item-detail-field-total-quantity", productItem.totalQuantity],
    [
      "product-item-detail-field-remaining-quantity",
      productItem.remainingQuantity
    ],
    [
      "product-item-detail-field-purchase-price",
      productItem.purchasePrice ? formatPrice(productItem.purchasePrice) : "-"
    ],
    [
      "product-item-detail-field-sales-price",
      productItem.salesPrice ? formatPrice(productItem.salesPrice) : "-"
    ],
    ["product-item-detail-field-lot-number", productItem.lotNumber],
    [
      "product-item-detail-field-weight",
      productItem.weight ? `${productItem.weight} kg` : "-"
    ],
    [
      "product-item-detail-field-height",
      productItem.height ? `${productItem.height} cm` : "-"
    ],
    [
      "product-item-detail-field-width",
      productItem.width ? `${productItem.width} cm` : "-"
    ],
    ["product-item-detail-field-color", productItem.color],
    [
      "product-item-detail-field-thickness",
      productItem.thickness ? `${productItem.thickness} cm` : "-"
    ],
    ["product-item-detail-field-pack-size", productItem.packSize],
    [
      "product-item-detail-field-expiration-date",
      productItem.expirationDate ? formatDate(productItem.expirationDate) : "-"
    ],
    [
      "product-item-detail-field-production-date",
      productItem.productionDate ? formatDate(productItem.productionDate) : "-"
    ],
    ["product-item-detail-field-created-at", formatDate(productItem.createdAt)],
    ["product-item-detail-field-updated-at", formatDate(productItem.updatedAt)],
    ["product-item-detail-field-last-updated-by", productItem.lastUpdatedBy],
    ["product-item-detail-field-store-id", productItem.storeId],
    ["product-item-detail-field-company-id", productItem.companyId]
  ];
};
